import classcat from "classcat";
import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Accordion, { AccordionItemType } from "@is/components/Accordion";
import { TranslationContext } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { EventData, TrackingEventTypes } from "@is/services/EventTracking";
import { Style } from "@is/types";

import css from "./DescriptionAccordion.module.css";

type Props = {
  styleDetails: Style;
  eventData?: EventData;
};

const DescriptionAccordion = ({ styleDetails, eventData }: Props) => {
  const { t } = useTranslation(TranslationContext.ProductDetails);

  const { updateViewState } = useContext(ViewStateContext).content;

  const handleAccordionClick = useCallback(() => {
    updateViewState({
      showSearchAndFooterDrawers: false,
    });
  }, [updateViewState]);

  const accordionItems = useMemo(() => {
    const items: AccordionItemType[] = [];

    items.push({
      id: t("PRODUCT DESCRIPTION"),
      header: t("PRODUCT DESCRIPTION"),
      content: (
        <div className={classcat(["text-label-base", css.content])}>
          {`${styleDetails?.description} 
          
          ${t("Article number")}: ${styleDetails?.id}`}
        </div>
      ),
      collapsed: true,
      trackingData: {
        eventType: TrackingEventTypes.ProductDescriptionOpened,
        data: eventData,
        createdAt: new Date().toISOString(),
      },
    });

    if (styleDetails?.materialComposition?.length) {
      const materialComposition = styleDetails.materialComposition?.map(
        (item, index) => {
          if (item.name === item.percentage) {
            return <div key={index}>{item.name}</div>;
          }
          return <div key={index}>{`${item.name}: ${item.percentage}%`}</div>;
        }
      );

      items.push({
        id: t("COMPOSITIONS & WASHING INSTRUCTIONS"),
        header: t("COMPOSITIONS & WASHING INSTRUCTIONS"),
        content: (
          <div className={classcat(["text-label-base", css.content])}>
            {materialComposition}
          </div>
        ),
        collapsed: true,
        trackingData: {
          eventType: TrackingEventTypes.MaterialCompositionOpened,
          data: eventData,
          createdAt: new Date().toISOString(),
        },
      });
    }

    return items;
  }, [eventData, styleDetails, t]);

  return (
    <Accordion items={accordionItems} animated onClick={handleAccordionClick} />
  );
};

export default DescriptionAccordion;
